import React, { useState } from 'react';
import { PricingHeader, PricingHeaderCreate } from '../types';

interface PricingHeaderFormProps {
  initialData?: PricingHeader | null;
  onSubmit: (data: PricingHeaderCreate) => void;
  onCancel: () => void;
}

const PricingHeaderForm: React.FC<PricingHeaderFormProps> = ({
  initialData,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<PricingHeaderCreate>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category: initialData?.category || '',
    region: initialData?.region || '',
    is_standard: initialData?.is_standard || false,
    effective_date: initialData?.effective_date 
      ? new Date(initialData.effective_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    expiry_date: initialData?.expiry_date 
      ? new Date(initialData.expiry_date).toISOString().split('T')[0]
      : '',
    created_by: initialData?.created_by || 'current_user',
    is_active: initialData?.is_active !== undefined ? initialData.is_active : true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.effective_date) {
      newErrors.effective_date = 'Effective date is required';
    }

    if (formData.expiry_date && formData.expiry_date <= formData.effective_date) {
      newErrors.expiry_date = 'Expiry date must be after effective date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Convert dates to ISO format for the backend
      const submitData = {
        ...formData,
        effective_date: formData.effective_date ? new Date(formData.effective_date).toISOString() : '',
        expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : undefined
      };
      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`form-control ${errors.name ? 'error' : ''}`}
              placeholder="Enter pricing header name"
            />
            {errors.name && <div className="form-error">{errors.name}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Category</label>
            <input
              type="text"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter category"
            />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows={3}
          placeholder="Enter description"
        />
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Region</label>
            <input
              type="text"
              name="region"
              value={formData.region}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter region"
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Created By</label>
            <input
              type="text"
              name="created_by"
              value={formData.created_by}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter creator name"
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Effective Date *</label>
            <input
              type="date"
              name="effective_date"
              value={formData.effective_date}
              onChange={handleChange}
              className={`form-control ${errors.effective_date ? 'error' : ''}`}
            />
            {errors.effective_date && <div className="form-error">{errors.effective_date}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Expiry Date</label>
            <input
              type="date"
              name="expiry_date"
              value={formData.expiry_date}
              onChange={handleChange}
              className={`form-control ${errors.expiry_date ? 'error' : ''}`}
            />
            {errors.expiry_date && <div className="form-error">{errors.expiry_date}</div>}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="is_standard"
                checked={formData.is_standard}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              Standard Pricing
            </label>
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              Active
            </label>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default PricingHeaderForm;